import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginWithGoogle, getCurrentUserInfo, signInWithEmail, signUpWithEmail, confirmSignUpCode } from '../../services/authService';
import { LanguageSwitcher } from '../layout/LanguageSwitcher';
import './LoginPage.css';

/**
 * Login page component
 * Redirects to Cognito Hosted UI for authentication (OAuth flow)
 */
function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await getCurrentUserInfo();
        if (user) {
          // User is already authenticated, redirect to chat
          navigate('/', { replace: true });
        }
      } catch (error) {
        // Not authenticated, stay on login page
        console.log('Not authenticated, showing login');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      console.error('Google sign in failed:', err);
      setError(t('auth.google_login_error'));
      setLoading(false);
    }
  };

  const handleEmailPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      if (isSignUp) {
        await signUpWithEmail(email, password);
        setShowEmailForm(false);
        setShowVerificationForm(true);
        setPassword('');
      } else {
        await signInWithEmail(email, password);
        navigate('/');
      }
    } catch (err: any) {
      console.error('Email/password error:', err);
      setError(err.message || t('auth.auth_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await confirmSignUpCode(email, verificationCode);
      alert(t('auth.email_verified'));
      setShowVerificationForm(false);
      setShowEmailForm(true);
      setIsSignUp(false);
      setVerificationCode('');
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || t('auth.verification_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="language-switcher-container">
          <LanguageSwitcher />
        </div>
        <div className="login-card">
          <h1>{t('auth.welcome')}</h1>
          <p className="subtitle">{t('auth.use_cognito')}</p>

          {showVerificationForm ? (
            <form className="email-form" onSubmit={handleVerificationSubmit}>
              <h3>{t('auth.verify_email')}</h3>
              <p className="form-hint" dangerouslySetInnerHTML={{ __html: t('auth.verification_hint', { email }) }} />

              {error && <div className="error-message">{error}</div>}

              <input
                type="text"
                placeholder={t('auth.verification_code')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                disabled={submitting}
                maxLength={6}
              />

              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? t('auth.verifying') : t('auth.verify_button')}
              </button>

              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setShowVerificationForm(false);
                  setShowEmailForm(false);
                  setError(null);
                  setVerificationCode('');
                }}
                disabled={submitting}
              >
                {t('auth.back_to_login')}
              </button>
            </form>
          ) : showEmailForm ? (
            <form className="email-form" onSubmit={handleEmailPasswordSubmit}>
              <h3>{isSignUp ? t('auth.create_account') : t('auth.sign_in')}</h3>

              {error && <div className="error-message">{error}</div>}

              <input
                type="email"
                placeholder={t('auth.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={submitting}
              />

              <input
                type="password"
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={submitting}
              />

              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? t('auth.processing') : (isSignUp ? t('auth.sign_up') : t('auth.sign_in'))}
              </button>

              <button
                type="button"
                className="btn-link"
                onClick={() => setIsSignUp(!isSignUp)}
                disabled={submitting}
              >
                {isSignUp ? t('auth.already_have_account') : t('auth.need_account')}
              </button>

              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setShowEmailForm(false);
                  setError(null);
                }}
                disabled={submitting}
              >
                {t('auth.back_to_options')}
              </button>
            </form>
          ) : (
            <>
              <button
                className="btn-google"
                onClick={handleGoogleSignIn}
                disabled={loading}
                type="button"
              >
                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('auth.sign_in_google')}
              </button>

              <div className="login-divider">
                <span>{t('auth.or')}</span>
              </div>

              <button
                className="btn-primary"
                onClick={() => setShowEmailForm(true)}
                disabled={loading}
                type="button"
              >
                {t('auth.sign_in_email')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
